/**
 * #763 — Board-top legend (the unshipped THE-GRID-REDESIGN.md "legend strip
 * at board top" deliverable). One slim wrap-friendly strip that decodes the
 * board's visual language for someone seeing it the first time: risk rings,
 * the repair-trip treatment, device short codes (hover for the plain-English
 * name from ASSEMBLY_VOCAB), the paper-city note, and what "pts" means.
 *
 * Collapsible, and the choice follows the user (server-backed preference,
 * not localStorage) — a veteran collapses it once and it stays a one-line
 * "Legend" affordance everywhere they sign in.
 */
import { useState } from "react";
import { ChevronDown, ChevronUp, Wrench } from "lucide-react";
import { assemblyName, ASSEMBLY_VOCAB } from "@/lib/vocabulary";
import { useUserPreference } from "@/lib/user-preference";
import { cn } from "@/lib/utils";

/** The codes worth legend space — the ones that actually appear on cards. */
const LEGEND_CODES = ["rpz", "dc", "dcva", "dcda", "pvb"] as const;

function Item({ children, title }: { children: React.ReactNode; title?: string }) {
    return (
        <span
            className="inline-flex items-center gap-1.5 whitespace-nowrap text-[11px] text-foreground/75"
            title={title}
        >
            {children}
        </span>
    );
}

export function GridLegend() {
    const [collapsed, setCollapsed] = useUserPreference<boolean>("grid_legend_collapsed", false);
    // #828 M8: hover-only decode was undiscoverable and dead on touch —
    // tapping any code expands the plain-English list inline.
    const [codesOpen, setCodesOpen] = useState(false);

    const allCodes = Object.values(ASSEMBLY_VOCAB)
        .map((e) => `${e.short} = ${e.name}`)
        .join("\n");

    return (
        <div className="rounded-lg border bg-card px-3 py-1.5" data-testid="grid-legend">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                <button
                    type="button"
                    onClick={() => setCollapsed(!collapsed)}
                    className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-[.1em] text-foreground/50 hover:text-foreground/80"
                    aria-expanded={!collapsed}
                >
                    Legend
                    {collapsed ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3" />}
                </button>
                {!collapsed && (
                    <>
                        <Item title="A colored ring around a card is a live-status signal from the field">
                            <span aria-hidden className="h-3 w-3 rounded-[4px] ring-2 ring-amber-500" />
                            <span>running long</span>
                            <span aria-hidden className="ml-1 h-3 w-3 rounded-[4px] ring-2 ring-red-500" />
                            <span>overdue</span>
                        </Item>
                        <Item title="Repair and replacement trips get an amber accent + wrench so a lane reads test, test, REPAIR at a glance">
                            <Wrench aria-hidden className="h-3 w-3 text-amber-600 dark:text-amber-400" />
                            <span>repair trip</span>
                        </Item>
                        {/* #975 — new timeline grammar. These swatches are
                            hand-authored to match the card, so they have to be
                            kept in step with day-swimlane.tsx by hand. */}
                        <Item title="A dashed card at the edge of a lane means that tech has stops scrolled out of view this way. Click it to jump to them.">
                            <span
                                aria-hidden
                                className="h-3 w-5 rounded-[4px] border border-dashed border-amber-500 bg-amber-50 dark:bg-amber-950"
                                style={{ borderLeftWidth: 3, borderLeftStyle: "solid", borderLeftColor: "#378ADD" }}
                            />
                            <span>off-screen work</span>
                        </Item>
                        <Item title="A dashed line before a tech's first stop means the day simply starts later — nothing is missing from the lane.">
                            <span aria-hidden className="h-0 w-5 border-t border-dashed border-foreground/40" />
                            <span>late start</span>
                        </Item>
                        <Item title={allCodes}>
                            <span className="font-semibold text-foreground/60">Devices:</span>
                            {LEGEND_CODES.map((code) => (
                                <button
                                    key={code}
                                    type="button"
                                    className="rounded bg-muted px-1 font-semibold hover:bg-primary/15"
                                    title={assemblyName(code)}
                                    onClick={() => setCodesOpen((o) => !o)}
                                >
                                    {ASSEMBLY_VOCAB[code]!.short}
                                </button>
                            ))}
                            <button
                                type="button"
                                className="text-foreground/50 underline-offset-2 hover:text-foreground hover:underline"
                                onClick={() => setCodesOpen((o) => !o)}
                                aria-expanded={codesOpen}
                            >
                                {codesOpen ? "(hide names)" : "(tap a code for its name)"}
                            </button>
                        </Item>
                        <Item title="Paper-filing city — test reports are filed on paper, so there is no water-account number to track">
                            <span className={cn("text-muted-foreground")}>no acct — paper</span>
                            <span className="text-muted-foreground">= paper-filing city</span>
                        </Item>
                        <Item title="Points are the job's value on the pay chart. Each tech's lane meter fills toward their daily points target — green has room, amber is close, red is over.">
                            <span className="rounded-full bg-primary/15 px-1.5 font-extrabold tabular-nums text-primary ring-1 ring-primary/25">
                                pts
                            </span>
                            <span>job value · lane meter fills to the tech's daily target</span>
                        </Item>
                    </>
                )}
            </div>
            {!collapsed && codesOpen && (
                <div
                    className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 border-t pt-1 text-[11px] text-foreground/70"
                    data-testid="legend-code-names"
                >
                    {LEGEND_CODES.map((code) => (
                        <span key={code}>
                            <span className="font-semibold">{ASSEMBLY_VOCAB[code]!.short}</span> ={" "}
                            {ASSEMBLY_VOCAB[code]!.name}
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
}
